/* Shared context for the mock modules: state, clock, session, RBAC, errors, ids, audit. */
import { round2 } from "../../domain/payroll";
import { countryOf } from "../../domain/country";
import { ApiError } from "../provider";
import type * as T from "../types";
import type { Mailer } from "./mailer";
import { createDemoStates, createState, PLATFORM, SEED, type MockState, type SeedFile } from "./state";

export interface MockClock { today: string; now: string }

/* ---------- RBAC (API.md table, mirrors api/internal/auth/rbac.go) ---------- */
export type Perm =
  | "authenticated" | "tenant" | "settings.read" | "settings.write" | "employees.read" | "employees.one" | "employees.write"
  | "files" | "payroll.read" | "payroll.payslip" | "payroll.ops" | "payroll.approve" | "leave.list" | "leave.create"
  | "leave.decide" | "leave.balance" | "attendance.day" | "attendance.month" | "attendance.check" | "biometric"
  | "loans.list" | "loans.decide" | "letters.list" | "letters.issue" | "expenses.list" | "expenses.decide" | "hr.modules" | "sites.read"
  | "gov.fees" | "audit" | "approvals" | "notifications" | "billing" | "ai.read" | "ai.extract" | "ai.chat" | "ai.update"
  | "reports" | "admin";

const o = "owner", h = "hr", a = "accountant", e = "employee", A = "admin";
export const MATRIX: Record<Perm, readonly T.Role[]> = {
  authenticated: [o, h, a, e, A],
  tenant: [o, h, a, e],
  "settings.read": [o, h, a],
  "settings.write": [o, h],
  "employees.read": [o, h, a],
  "employees.one": [o, h, a, e],
  "employees.write": [o, h],
  files: [o, h, a, e],
  "payroll.read": [o, h, a],
  "payroll.payslip": [o, h, a, e],
  "payroll.ops": [o, h, a],
  "payroll.approve": [o, h],
  "leave.list": [o, h, e],
  "leave.create": [o, h, e],
  "leave.decide": [o, h],
  "leave.balance": [o, h, a, e],
  "attendance.day": [o, h],
  "attendance.month": [o, h, e],
  "attendance.check": [h, e],
  biometric: [o, h],
  "loans.list": [o, h, a, e],
  "loans.decide": [o, h, a],
  "letters.list": [o, h, e],
  "letters.issue": [o, h],
  "expenses.list": [o, h, a, e],
  "expenses.decide": [o, h, a],
  "hr.modules": [o, h],
  "sites.read": [o, h, a],
  "gov.fees": [o, h, a],
  audit: [o],
  approvals: [o, h, a],
  notifications: [o, h, a, e],
  billing: [o],
  "ai.read": [o, h, a],
  "ai.extract": [o, h, a, e],
  "ai.chat": [o, h, a, e],
  "ai.update": [o],
  reports: [o, h, a],
  admin: [A],
};
export const allowed = (role: T.Role, p: Perm) => MATRIX[p].includes(role);

/* ---------- errors (same codes/messages as the Go API) ---------- */
export const E = {
  rateLimited: (msg = "too many requests — try again later") => new ApiError(429, "rate_limited", msg),
  validation: (fields: Record<string, string>) => new ApiError(422, "validation", "validation failed", fields),
  field: (k: string, msg: string) => new ApiError(422, "validation", "validation failed", { [k]: msg }),
  notFound: (msg = "not found") => new ApiError(404, "not_found", msg),
  conflict: (msg: string) => new ApiError(409, "conflict", msg),
  forbidden: (msg = "your role cannot access this resource") => new ApiError(403, "forbidden", msg),
  unauthorized: (msg = "missing token") => new ApiError(401, "unauthorized", msg),
  badRequest: (msg: string) => new ApiError(400, "bad_request", msg),
  tooLarge: (msg: string) => new ApiError(413, "bad_request", msg),
};

/* ---------- runtime type guards (inputs may come from untyped UI code) ---------- */
export const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
export const isInt = (v: unknown): v is number => isNum(v) && Number.isInteger(v) && Math.abs(v) <= 1e12;
export const str = (v: unknown): string => (typeof v === "string" ? v : "");
export const has = (o: object, k: string) => (o as Record<string, unknown>)[k] != null;

/** Go strconv.FormatFloat(f,'f',2) after round2 — money in CSVs/messages. */
export const fmtMoney = (n: number) => round2(n).toFixed(2);
/** Go strconv.FormatFloat(f,'f',-1). */
export const fmtNum = (n: number) => String(n);
export const pad = (n: number, w: number) => String(n).padStart(w, "0");

export function randomHex(bytes: number): string {
  const b = new Uint8Array(bytes);
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(b);
  else for (let i = 0; i < bytes; i++) b[i] = Math.floor(Math.random() * 256);
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}
export function uuid(): string {
  const x = randomHex(16);
  return `${x.slice(0, 8)}-${x.slice(8, 12)}-4${x.slice(13, 16)}-a${x.slice(17, 20)}-${x.slice(20, 32)}`;
}

/** Keeps [A-Za-z0-9._-], max 100 chars (mirrors Go SanitizeName). */
export function sanitizeName(name: string): string {
  const base = name.replace(/\\/g, "/").split("/").pop() || "";
  let n = base.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/-{2,}/g, "-").replace(/^[-.]+|[-.]+$/g, "");
  if (n.length > 100) n = n.slice(n.length - 100);
  return n || "file";
}

const TOKEN_PREFIX = "mock.";

export class Ctx {
  /** State of the tenant the current caller belongs to (the seed company for admins / anonymous calls). */
  state: MockState;
  /** The seed company's state; owns the platform-wide (shared) fields. */
  root: MockState;
  /** Every company: the seed one plus companies created by self-signup. */
  tenantStates = new Map<string, MockState>();
  clock: MockClock;
  token: string | null = null;
  readonly seed: SeedFile;
  /** Wall clock in ms (token expiry, rate limits) — injectable in tests. */
  wallNow: () => number = () => Date.now();
  /** Mock mailer (set by MockProvider). */
  mail!: Mailer;
  /** Base URL used in email links. */
  baseUrl = (() => { try { const l = globalThis.location; return l && l.origin && l.origin !== "null" ? l.origin + l.pathname.replace(/\/index\.html$/, "/").replace(/\/$/, "") : "http://localhost:5173"; } catch { return "http://localhost:5173"; } })();

  constructor(seed: SeedFile = SEED, clock?: Partial<MockClock>) {
    this.seed = seed;
    this.root = this.state = createState(seed);
    this.tenantStates.set(this.root.tenantId, this.root);
    for (const d of createDemoStates(this.root, seed)) this.tenantStates.set(d.tenantId, d);
    this.clock = Ctx.mkClock(seed, clock);
  }

  /** The current tenant's GCC country (Go s.country) and currency code (Go s.curOf). */
  get country(): T.CountryCode { return countryOf(this.state.settings.tenant.country).code; }
  get cur(): string { return countryOf(this.state.settings.tenant.country).currency.code; }

  stateFor(tid: string | undefined): MockState | undefined { return tid ? this.tenantStates.get(tid) : undefined; }
  /** A user as sign-in and /me return it: with the company's country (none for the platform admin). */
  publicUser(u: T.User): T.User {
    const { country: _drop, ...rest } = structuredClone(u);
    void _drop;
    if (u.tenantId === PLATFORM) return rest;
    return { ...rest, country: countryOf(this.stateFor(u.tenantId)?.settings.tenant.country).code };
  }
  /** Switch to a tenant's state (internal; principal() does this automatically). */
  use(tid: string) { this.state = this.tenantStates.get(tid) ?? this.root; }

  static mkClock(seed: SeedFile, clock?: Partial<MockClock>): MockClock {
    const today = clock?.today ?? (clock?.now ? clock.now.slice(0, 10) : seed.today);
    const now = clock?.now ?? (today + "T" + seed.now.slice(11));
    return { today, now };
  }

  reset() {
    this.root = this.state = createState(this.seed);
    this.tenantStates = new Map([[this.root.tenantId, this.root], ...createDemoStates(this.root, this.seed).map((d): [string, MockState] => [d.tenantId, d])]);
    this.token = null;
  }

  get today() { return this.clock.today; }
  get now() { return this.clock.now; }
  get nowHm() { return this.clock.now.slice(11, 16); }
  get tid() { return this.state.tenantId; }

  /* ---------- session ---------- */
  issueToken(u: T.User): string { return TOKEN_PREFIX + u.id + "." + randomHex(8) + "~" + (this.root.tokenVersions[u.id] ?? 0); }

  userFromToken(tok: string | null): T.User | null {
    if (!tok || !tok.startsWith(TOKEN_PREFIX) || this.root.revoked.has(tok)) return null;
    let rest = tok.slice(TOKEN_PREFIX.length);
    let ver = 0;
    const tilde = rest.lastIndexOf("~");
    if (tilde >= 0) { ver = Number(rest.slice(tilde + 1)) || 0; rest = rest.slice(0, tilde); }
    const id = rest.slice(0, rest.lastIndexOf("."));
    if ((this.root.tokenVersions[id] ?? 0) !== ver) return null; // revoked by a password reset/change
    return this.root.users.find((u) => u.id === id) ?? null;
  }

  /** Authenticate the current token and check the permission (401 / 403). */
  principal(perm: Perm = "authenticated"): T.User {
    if (!this.token) throw E.unauthorized("missing token");
    const u = this.userFromToken(this.token);
    if (!u) throw E.unauthorized("invalid or expired token");
    this.state = u.tenantId === PLATFORM ? this.root : (this.tenantStates.get(u.tenantId) ?? this.root);
    if (!allowed(u.role, perm)) throw E.forbidden();
    return u;
  }

  ownEid(u: T.User): number {
    if (u.employeeId == null) throw E.forbidden("user is not linked to an employee");
    return u.employeeId;
  }
  isEmployee(u: T.User) { return u.role === "employee"; }
  ownsKey(u: T.User, key: string) { return key.startsWith(u.tenantId + "/") && !key.includes(".."); }

  /* ---------- ids + audit ---------- */
  nextId(name: string, start: number, maxExisting: () => number): number {
    let n = this.state.counters[name];
    if (n == null) n = Math.max(start - 1, maxExisting());
    n++;
    this.state.counters[name] = n;
    return n;
  }

  audit(u: T.User, action: string, entity: string, entityId: string, detail: string) {
    const plat = u.tenantId === PLATFORM;
    const log = plat ? this.state.platformAudit : this.state.audit;
    const n = this.nextId(plat ? "audit:platform" : "audit", 1, () => log.reduce((m, x) => Math.max(m, Number(/(\d+)$/.exec(x.id)?.[1] ?? 0)), 0));
    log.push({ id: "a" + pad(n, 6), at: this.now, actor: u.name, action, entity, entityId, detail });
  }

  /* ---------- employees ---------- */
  employeesSorted(): T.Employee[] { return this.state.employees.slice().sort((x, y) => x.id - y.id); }
  active(): T.Employee[] { return this.employeesSorted().filter((x) => x.status !== "Terminated"); }
  emp(id: number): T.Employee {
    const x = this.state.employees.find((v) => v.id === id);
    if (!x) throw E.notFound("employee not found");
    return x;
  }

  isPlatform(u: T.User) { return u.tenantId === PLATFORM; }
}
